// composables/useEvents.ts
import { onUnmounted } from 'vue';
import {
  useEventStore,
  type EventName,
  type EventPayload,
} from 'src/stores/event';

export function useEvents() {
  const eventStore = useEventStore();
  const unsubscribers: (() => void)[] = [];

  // Listen to an event with automatic cleanup
  function listen<T extends EventName>(
    eventName: T,
    callback: (payload: EventPayload<T>) => void,
  ) {
    const unsubscribe = eventStore.on(eventName, callback);
    unsubscribers.push(unsubscribe);
    return unsubscribe;
  }

  // Emit an event
  function emit<T extends EventName>(eventName: T, payload: EventPayload<T>) {
    eventStore.emit(eventName, payload);
  }

  // Get last event value
  function getLastEvent<T extends EventName>(eventName: T) {
    return eventStore.getLastEvent(eventName);
  }

  // Cleanup all listeners when component unmounts
  onUnmounted(() => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  });

  return {
    listen,
    emit,
    getLastEvent,
  };
}
